import { AxiosHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import {
  createProductDemoData,
  PRODUCT_DEMO_IDS,
} from '~modules/product-demo/product-demo-data';

type DemoApiRequest = {
  endpoint: string;
  params?: Record<string, unknown>;
  userId?: number | null;
};

type DemoApiResult<T> =
  | {
      found: true;
      response: AxiosResponse<T>;
    }
  | {
      found: false;
    };

const normalizeEndpoint = (endpoint: string) =>
  endpoint.replace(/^\/+/, '').replace(/\/+$/, '');

const response = <T>(
  endpoint: string,
  data: T,
  params?: Record<string, unknown>,
): AxiosResponse<T> => ({
  data,
  status: 200,
  statusText: 'OK',
  headers: {},
  config: {
    url: endpoint,
    params,
    headers: new AxiosHeaders(),
  } as InternalAxiosRequestConfig,
});

const asArray = (value: unknown) => {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string' && value.includes(',')) return value.split(',');
  if (value === undefined || value === null || value === '') return [];
  return [String(value)];
};

const matchesParam = (actual: unknown, expected: unknown) => {
  const expectedValues = asArray(expected);
  if (!expectedValues.length) return true;

  return expectedValues.includes(String(actual));
};

const applyListParams = <T extends Record<string, unknown>>(
  items: T[],
  params?: Record<string, unknown>,
) => {
  let result = [...items];

  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (['page', 'limit', 'sort', 'sort_by'].includes(key)) return;
    if (value === undefined || value === null || value === '') return;

    if (key.endsWith('_from') || key.endsWith('_till')) {
      const field = key.replace(/_(from|till)$/, '');
      const itemTime = (item: T) => Date.parse(String(item[field]));
      const filterTime = Date.parse(String(value));

      if (Number.isNaN(filterTime)) return;

      result = result.filter((item) => {
        const time = itemTime(item);
        if (Number.isNaN(time)) return false;

        return key.endsWith('_from') ? time >= filterTime : time <= filterTime;
      });
      return;
    }

    result = result.filter((item) => matchesParam(item[key], value));
  });

  const sortBy = params?.sort_by;
  const sort = params?.sort;

  if (typeof sortBy === 'string') {
    result.sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (aValue === bValue) return 0;

      const direction = sort === 'asc' ? 1 : -1;
      const aTime = Date.parse(String(aValue));
      const bTime = Date.parse(String(bValue));

      if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
        return (aTime - bTime) * direction;
      }

      return String(aValue).localeCompare(String(bValue)) * direction;
    });
  }

  const limit = Number(params?.limit);

  if (Number.isFinite(limit) && limit > 0) {
    result = result.slice(0, limit);
  }

  return result;
};

const getNumericId = (endpoint: string, pattern: RegExp) => {
  const match = endpoint.match(pattern);
  if (!match?.[1]) return null;

  const id = Number(match[1]);
  return Number.isFinite(id) ? id : null;
};

export const isProductDemoWriteWhitelisted = (endpoint: string) => {
  const normalized = normalizeEndpoint(endpoint);

  return normalized === 'auth/signout';
};

export const isProductDemoDomainEndpoint = (endpoint: string) => {
  const normalized = normalizeEndpoint(endpoint);

  return [
    'user/meta',
    'lessor/properties',
    'lessor/leases',
    'lessor/tenants',
    'lessor/invoices',
    'lessor/payment-records',
    'reminder-actions',
    'reminder-templates',
    'lessor/comm-events',
    'edit-history/',
    'reminders/',
    'leases/',
    'sms/lessor/test',
  ].some((prefix) => normalized === prefix || normalized.startsWith(prefix));
};

export const productDemoApiRouter = {
  resolve<T>({ endpoint, params, userId }: DemoApiRequest): DemoApiResult<T> {
    const normalized = normalizeEndpoint(endpoint);
    const demo = createProductDemoData({
      userId: userId ?? PRODUCT_DEMO_IDS.userMeta,
    });

    if (normalized === 'user/meta' || normalized === 'tenant/user/meta') {
      return {
        found: true,
        response: response(endpoint, demo.userMeta as T, params),
      };
    }

    if (normalized === 'lessor/properties') {
      return {
        found: true,
        response: response(
          endpoint,
          applyListParams(demo.properties, params) as T,
          params,
        ),
      };
    }

    const propertyId = getNumericId(
      normalized,
      /^lessor\/properties\/(-?\d+)$/,
    );
    if (propertyId !== null) {
      return {
        found: true,
        response: response(
          endpoint,
          demo.properties.find((item) => item.id === propertyId) as T,
          params,
        ),
      };
    }

    const propertyTenantsId = getNumericId(
      normalized,
      /^lessor\/properties\/(-?\d+)\/tenants$/,
    );
    if (propertyTenantsId !== null) {
      return {
        found: true,
        response: response(
          endpoint,
          demo.tenants.filter((tenant) =>
            tenant.property_ids.includes(propertyTenantsId),
          ) as T,
          params,
        ),
      };
    }

    const propertyLeasesId = getNumericId(
      normalized,
      /^lessor\/properties\/(-?\d+)\/leases$/,
    );
    if (propertyLeasesId !== null) {
      return {
        found: true,
        response: response(
          endpoint,
          demo.leases.filter(
            (lease) => lease.property_id === propertyLeasesId,
          ) as T,
          params,
        ),
      };
    }

    if (normalized === 'lessor/leases') {
      return {
        found: true,
        response: response(
          endpoint,
          applyListParams(demo.leases, params) as T,
          params,
        ),
      };
    }

    const leaseId = getNumericId(normalized, /^lessor\/leases\/(-?\d+)$/);
    if (leaseId !== null) {
      return {
        found: true,
        response: response(
          endpoint,
          demo.leases.find((item) => item.id === leaseId) as T,
          params,
        ),
      };
    }

    const leaseTenantsId = getNumericId(
      normalized,
      /^lessor\/leases\/(-?\d+)\/tenants$/,
    );
    if (leaseTenantsId !== null) {
      return {
        found: true,
        response: response(
          endpoint,
          demo.tenants.filter((tenant) =>
            tenant.lease_ids.includes(leaseTenantsId),
          ) as T,
          params,
        ),
      };
    }

    if (normalized === 'lessor/tenants') {
      return {
        found: true,
        response: response(
          endpoint,
          applyListParams(demo.tenants, params) as T,
          params,
        ),
      };
    }

    const tenantId = getNumericId(normalized, /^lessor\/tenants\/(-?\d+)$/);
    if (tenantId !== null) {
      return {
        found: true,
        response: response(
          endpoint,
          demo.tenants.find((item) => item.id === tenantId) as T,
          params,
        ),
      };
    }

    if (normalized === 'lessor/invoices') {
      return {
        found: true,
        response: response(
          endpoint,
          applyListParams(demo.invoices, params) as T,
          params,
        ),
      };
    }

    const invoiceId = getNumericId(normalized, /^lessor\/invoices\/(-?\d+)$/);
    if (invoiceId !== null) {
      return {
        found: true,
        response: response(
          endpoint,
          demo.invoices.find((item) => item.id === invoiceId) as T,
          params,
        ),
      };
    }

    if (normalized === 'lessor/payment-records') {
      return {
        found: true,
        response: response(
          endpoint,
          applyListParams(demo.payments, params) as T,
          params,
        ),
      };
    }

    const paymentId = getNumericId(
      normalized,
      /^lessor\/payment-records\/(-?\d+)$/,
    );
    if (paymentId !== null) {
      return {
        found: true,
        response: response(
          endpoint,
          demo.payments.find((item) => item.id === paymentId) as T,
          params,
        ),
      };
    }

    if (normalized === 'reminder-actions') {
      return {
        found: true,
        response: response(
          endpoint,
          applyListParams(demo.reminderActions, params) as T,
          params,
        ),
      };
    }

    const reminderActionId = getNumericId(
      normalized,
      /^reminder-actions\/(-?\d+)$/,
    );
    if (reminderActionId !== null) {
      return {
        found: true,
        response: response(
          endpoint,
          demo.reminderActions.find(
            (item) => item.id === reminderActionId,
          ) as T,
          params,
        ),
      };
    }

    if (normalized === 'reminder-templates') {
      return {
        found: true,
        response: response(
          endpoint,
          applyListParams(demo.reminderTemplates, params) as T,
          params,
        ),
      };
    }

    const reminderTemplateId = getNumericId(
      normalized,
      /^reminder-templates\/(-?\d+)$/,
    );
    if (reminderTemplateId !== null) {
      return {
        found: true,
        response: response(
          endpoint,
          demo.reminderTemplates.find(
            (item) => item.id === reminderTemplateId,
          ) as T,
          params,
        ),
      };
    }

    if (normalized === 'lessor/comm-events') {
      return {
        found: true,
        response: response(
          endpoint,
          applyListParams(demo.commEvents, params) as T,
          params,
        ),
      };
    }

    const reminderCommEventsId = getNumericId(
      normalized,
      /^reminders\/(-?\d+)\/comm-events$/,
    );
    if (reminderCommEventsId !== null) {
      return {
        found: true,
        response: response(
          endpoint,
          demo.commEvents.filter(
            (event) => event.reminder_action_id === reminderCommEventsId,
          ) as T,
          params,
        ),
      };
    }

    const tenantInviteLeaseId = getNumericId(
      normalized,
      /^leases\/(-?\d+)\/tenant-invite\/link$/,
    );
    if (tenantInviteLeaseId !== null) {
      return {
        found: true,
        response: response(endpoint, demo.tenantInviteLink as T, params),
      };
    }

    if (
      /^edit-history\/(leases|properties|invoices)\/-?\d+$/.test(normalized)
    ) {
      return {
        found: true,
        response: response(endpoint, [] as T, params),
      };
    }

    if (normalized === 'sms/lessor/test') {
      return {
        found: true,
        response: response(
          endpoint,
          demo.reminderActions.find(
            (item) => item.id === PRODUCT_DEMO_IDS.reminderActionSent,
          ) as T,
          params,
        ),
      };
    }

    if (isProductDemoDomainEndpoint(normalized)) {
      return {
        found: true,
        response: response(endpoint, [] as T, params),
      };
    }

    return {
      found: false,
    };
  },
};
